import { clientToolNarrationPolicy } from './client-tools/registry';
import {
  classifyToolError,
  reportLoopError,
} from './global-ai-loop-error-telemetry';
import { ArtifactCache, TurnState } from './global-ai-turn-messages';
import type {
  TurnMessage,
  TurnStepEvent,
  TurnStepRequestBody,
} from './global-ai-turn-step-stream';
import type { ReasoningStepStatus } from './reasoning-trace';

const DEFAULT_MAX_STEPS = 6;

/**
 * The wire's two-field account of a finished server step, collapsed to the one
 * thing the reasoning trace draws.
 *
 * WHERE THE PRECEDENCE LIVES. `ok` and `outcome` answer different questions and
 * both stay on the wire — `ok` is "did it hard-fail", which other consumers
 * read, and `outcome` is how it concluded. A trace step has one question, so
 * the two are reconciled HERE, once, rather than at every read site.
 *
 *  - `refused` / `empty` → `inconclusive`. The step RAN and did not break, so
 *    `ok` is legitimately `true` — and drawing the success tick off that told
 *    a user whose headcount question was declined that the lookup worked.
 *  - `failed`, or `ok === false` → `error`. The two agree by construction;
 *    honouring either independently means a mismatch can only ever overstate
 *    a break we were told about, never hide one.
 *  - anything else, INCLUDING an absent `outcome` → `ok`'s own verdict. Most
 *    tools publish no outcome, and an older bundle catches an unfamiliar one
 *    back to `undefined`; both must render exactly as they did before.
 *
 * Module-private, and tested through `runTurnLoop` rather than directly: the
 * status only matters where it reaches `onStepEnd`, and knip gates unused
 * exports.
 */
const toStepStatus = (event: {
  ok?: boolean;
  outcome?: 'answered' | 'refused' | 'empty' | 'failed';
}): ReasoningStepStatus => {
  if (event.outcome === 'refused' || event.outcome === 'empty') {
    return 'inconclusive';
  }
  return event.ok === false || event.outcome === 'failed' ? 'error' : 'done';
};

export type RunTurnLoopParams = {
  userPrompt: string;
  priorMessages: TurnMessage[];
  /**
   * Built per step, not once per turn. `apply_filters` navigates mid-loop and
   * the reads are exempt from the post-outcome withhold, so a `current_page`
   * read can run after the route moved; a frozen context answered for the page
   * the user had left. A function because only the caller sees the live route.
   */
  context?: () => TurnStepRequestBody['context'];
  postStep: (p: {
    body: TurnStepRequestBody;
    onEvent: (e: TurnStepEvent) => void;
    onInvalidEnvelope?: (raw: string, reason: 'json' | 'schema') => void;
    signal?: AbortSignal;
  }) => Promise<void>;
  executeTool: (
    name: string,
    input: unknown,
    artifacts: ArtifactCache
  ) => Promise<{ resultForModel: string; endsTurn?: boolean }>;
  onText: (segmentId: string, fullText: string) => void;
  onStatus: (content: string) => void;
  onError: (message: string) => void;
  onToolCall: (id: string, name: string, input: unknown) => void;
  /**
   * Every server artifact as it streams in, BEFORE any tool consumes it.
   * The runner needs a look at `filter_response` payloads independently of
   * which tool (if any) references them — a turn's geography-taxonomy
   * mismatch rides on the artifact root, and `onAnswer` has to know about it
   * to stamp the narration's `offer:geo_tax` trigger.
   */
  onArtifact?: (artifact: {
    id: string;
    kind: 'filter_response' | 'chart_config';
    payload: unknown;
  }) => void;
  onStepStart?: (roundId: string) => void;
  onStepDescribe?: (roundId: string, text: string) => void;
  onStepTool?: (
    roundId: string,
    name: string,
    input: unknown,
    artifacts?: ArtifactCache
  ) => void;
  onStepEnd?: (roundId: string, status: ReasoningStepStatus) => void;
  onAnswer?: (roundId: string, text: string) => void;
  signal?: AbortSignal;
  maxSteps?: number;
  newSegmentId?: () => string;
};

export async function runTurnLoop(params: RunTurnLoopParams): Promise<void> {
  const maxSteps = params.maxSteps ?? DEFAULT_MAX_STEPS;
  const newSegmentId = params.newSegmentId ?? (() => crypto.randomUUID());
  const state = new TurnState(params.priorMessages, params.userPrompt);
  const artifacts = new ArtifactCache();

  for (let stepIndex = 0; ; stepIndex += 1) {
    if (stepIndex >= maxSteps) {
      params.onError('This turn took too many steps; please try rephrasing.');
      return;
    }

    const segmentId = newSegmentId();
    params.onStepStart?.(segmentId);
    let streamedText = '';
    // Offset into `streamedText` where the CURRENT step's narration begins. A
    // round can run several tools (text → search_filters → more text →
    // render_chart); each tool's step description is only the narration emitted
    // since the previous tool, so we advance this past every tool boundary and
    // slice from it. `streamedText` itself stays cumulative (the model history
    // and the final answer need the whole round's text).
    let narrationStart = 0;
    let pendingToolCall:
      | { id: string; name: string; input: unknown }
      | undefined;
    let errored: string | undefined;
    // The SAME server tool can run more than once in a round: the page-offer
    // grounding path emits its own `search_filters` step alongside a
    // model-issued one, and the per-step budget allows up to 4 calls. Ids keyed
    // on the tool name alone collided, so the repeat's entries merged into the
    // first one's in the reasoning trace.
    //
    // Two structures, one job each. `toolStepTotals` NAMES a step: it only ever
    // climbs, so sequential repeats get distinct ids (a live-open count would
    // fall back to zero after each `end` and re-issue the colliding first id).
    // `openToolStepIds` MATCHES an `end` to its `start` by popping the id that
    // start actually issued, so nothing here depends on how the backend
    // interleaves its steps.
    const toolStepTotals = new Map<string, number>();
    const openToolStepIds = new Map<string, string[]>();
    const toolStepId = (name: string, occurrence: number) =>
      occurrence <= 1
        ? `${segmentId}:${name}`
        : `${segmentId}:${name}#${occurrence}`;

    // Re-read for THIS step: see `context` on the params type.
    const stepContext = params.context?.();
    await params.postStep({
      body: {
        messages: state.messages(),
        stepIndex,
        ...(stepContext && { context: stepContext }),
      },
      signal: params.signal,
      onEvent: (event) => {
        if (event.type === 'text') {
          streamedText += event.content;
          params.onText(segmentId, streamedText);
          params.onStepDescribe?.(
            segmentId,
            streamedText.slice(narrationStart)
          );
        } else if (event.type === 'status') {
          params.onStatus(event.content);
        } else if (event.type === 'artifact') {
          artifacts.put(event.id, event.payload);
          params.onArtifact?.({
            id: event.id,
            kind: event.kind,
            payload: event.payload,
          });
        } else if (event.type === 'tool_call') {
          pendingToolCall = {
            id: event.id,
            name: event.name,
            input: event.input,
          };
        } else if (event.type === 'tool_step') {
          if (event.phase === 'start') {
            const occurrence = (toolStepTotals.get(event.name) ?? 0) + 1;
            toolStepTotals.set(event.name, occurrence);
            const stepId = toolStepId(event.name, occurrence);
            openToolStepIds.set(event.name, [
              ...(openToolStepIds.get(event.name) ?? []),
              stepId,
            ]);
            params.onStepStart?.(stepId);
            params.onStepTool?.(stepId, event.name, undefined, artifacts);
            // This server tool consumed the narration so far; the next step's
            // description starts from text emitted after it.
            narrationStart = streamedText.length;
          } else {
            // An `end` with no open `start` keeps the base id it resolved to
            // before this counter existed.
            const stepId =
              openToolStepIds.get(event.name)?.pop() ??
              `${segmentId}:${event.name}`;
            params.onStepEnd?.(stepId, toStepStatus(event));
          }
        } else if (event.type === 'error') {
          errored = event.content;
        }
      },
      onInvalidEnvelope: (_raw, reason) => {
        // Additive telemetry + reuse of the EXISTING error path: set the same
        // `errored` flag a server `error` event would, so the loop's existing
        // post-stream handling surfaces it and ends the turn. No new control
        // flow is introduced.
        void reportLoopError({
          source: 'sse_envelope',
          errorClass: reason,
          stepIndex,
        });
        errored = 'The assistant sent a malformed response — please try again.';
      },
    });

    if (errored) {
      params.onError(errored);
      return;
    }
    if (!pendingToolCall) {
      params.onAnswer?.(segmentId, streamedText);
      return; // text-only step → turn complete
    }

    params.onToolCall(
      pendingToolCall.id,
      pendingToolCall.name,
      pendingToolCall.input
    );
    params.onStepTool?.(
      segmentId,
      pendingToolCall.name,
      pendingToolCall.input,
      artifacts
    );
    narrationStart = streamedText.length;
    state.recordAssistantStep(streamedText, {
      id: pendingToolCall.id,
      name: pendingToolCall.name,
      argumentsJson: JSON.stringify(pendingToolCall.input ?? {}),
    });

    // Presentation policy for the round's streamed narration, declared by each
    // tool's registry definition (ClientToolNarrationPolicy) — `'drop'` for the
    // param-narration offer tools and `ask_user`, `'answer'` for anything the
    // registry doesn't know.
    const narrationPolicy = clientToolNarrationPolicy(pendingToolCall.name);

    // Terminal tools that APPEND A CHIP (offer_report's report chips) must
    // render their streamed narration ABOVE the chip — the explanation comes
    // first, then the button. The chip is appended during executeTool, so without
    // this the chip lands first and the narration reads as a trailing afterthought
    // below it. Flush the narration BEFORE running the tool.
    if (narrationPolicy === 'flush-before-chip' && streamedText.trim()) {
      params.onAnswer?.(segmentId, streamedText);
    }

    let result: { resultForModel: string; endsTurn?: boolean };
    try {
      result = await params.executeTool(
        pendingToolCall.name,
        pendingToolCall.input,
        artifacts
      );
      // A client tool can also RETURN an `ERROR: …` string (artifact-not-found,
      // unknown-tool). Detect it here and emit telemetry without altering what
      // gets recorded/sent back to the model.
      if (
        typeof result.resultForModel === 'string' &&
        result.resultForModel.startsWith('ERROR:')
      ) {
        void reportLoopError({
          source: 'tool_execution',
          toolName: pendingToolCall.name,
          errorClass: classifyToolError(result.resultForModel),
          stepIndex,
        });
      }
    } catch (err) {
      // A throw AFTER the turn aborted is cancellation fallout, not a tool
      // failure (e.g. the headless page-gen rethrows its AbortError): don't
      // spend a round feeding the model an ERROR the aborted next postStep
      // would discard, and don't report failure telemetry for a user stop.
      // Propagate instead — the runner handles an aborted turn silently.
      if (params.signal?.aborted) {
        throw err;
      }
      // Additive telemetry — does not change the existing ERROR-result flow.
      void reportLoopError({
        source: 'tool_execution',
        toolName: pendingToolCall.name,
        errorClass: classifyToolError(err),
        stepIndex,
      });
      // Catch-local, NOT the outer `result`: a thrown tool cannot end the turn,
      // and keeping the envelope out of `result` means there is no `endsTurn`
      // here to be tempted to check. A failed tool always feeds the error back
      // to the model and continues the loop.
      const errorResult = {
        resultForModel: `ERROR: ${
          err instanceof Error ? err.message : 'tool failed'
        }`,
      };
      params.onStepEnd?.(segmentId, 'error');
      state.recordToolResult(pendingToolCall.id, errorResult.resultForModel);
      continue;
    }
    params.onStepEnd?.(segmentId, 'done');
    state.recordToolResult(pendingToolCall.id, result.resultForModel);
    if (result.endsTurn) {
      // A terminal tool ends the turn while the round's narration is the
      // grounded answer. Surface it once (not as a duplicate step description),
      // but only under the `'answer'` policy: `'drop'` never surfaces a
      // preview, and `'flush-before-chip'` already surfaced it above. Empty
      // narration stays answer-less.
      if (streamedText.trim() && narrationPolicy === 'answer') {
        params.onAnswer?.(segmentId, streamedText);
      }
      return;
    }
  }
}
